import {
  CafeLocator,
  LinkButton,
} from "@bluebottlecoffee/design-system/components";
import { Configure, InstantSearch, useHits } from "react-instantsearch";
import { FunctionComponent, useState } from "react";
import {
  cafesIndexSearchableAttribute,
  standardizeIndexName,
} from "../lib/algolia/indices";
import { publicSearchClient } from "../lib/algolia/clients";
import { Cafe } from "../lib/sanity-schema";
import { CafeLocatorFullCopy } from "../lib/transformers/cafe-locator";
import { AlgoliaCafe } from "../lib/algolia/types";
import { CafeSearchResult } from "./CafeSearch/CafeSearchResult";

export type CafeDetailLocatorCopy = {
  headingCafeDetailLocator?: string;
  backToResultsLabel?: string;
};

interface CafeDetailLocatorProps {
  lang: string;
  region: string;
  copy: CafeLocatorFullCopy;
}

export const CafeDetailLocatorRender: FunctionComponent<
  CafeDetailLocatorProps
> = ({ lang, region, copy }) => {
  const [hitsLimit, setHitsLimit] = useState<number>(3);

  const {
    // remove the current cafe
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    hits: [_, ...cafes],
  } = useHits<AlgoliaCafe>();

  const viewMoreBtn = (
    <LinkButton
      text={copy.viewMoreText}
      onClick={() => setHitsLimit(hitsLimit + 3)}
    />
  );

  if (!cafes.length) return null;
  return (
    <CafeLocator
      copy={{
        ...copy,
        headline: copy.headingCafeDetailLocator,
      }}
      previewCafes={[]}
      searchResults={cafes.map((hit) => (
        <CafeSearchResult
          isModule
          key={hit.objectID}
          cafeCardProps={{
            imageAriaLabel: hit.image?.altText?.[lang] ?? "",
            newWindowWarning: copy.newWindowWarning,
          }}
          cafeHoursCopy={{
            opens: copy.opens,
            openTil: copy.openTil,
          }}
          algoliaHit={hit}
          lang={lang}
          region={region}
        />
      ))}
      totalResultsDisplay={hitsLimit}
      viewMoreButton={viewMoreBtn}
    />
  );
};

export const CafeDetailLocator: FunctionComponent<
  CafeDetailLocatorProps & {
    cafe: Cafe;
  }
> = ({ cafe, lang, ...props }) => {
  const aroundLatLng = `${cafe.geoLocation.lat}, ${cafe.geoLocation.lng}`;

  return (
    <InstantSearch
      indexName={standardizeIndexName("cafes")}
      searchClient={publicSearchClient}
      insights
    >
      <CafeDetailLocatorRender {...props} lang={lang} />
      <Configure
        aroundLatLng={aroundLatLng}
        hitsPerPage={999}
        clickAnalytics
        distinct
        restrictSearchableAttributes={cafesIndexSearchableAttribute(lang)}
      />
    </InstantSearch>
  );
};
